import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface GammaMarket {
  conditionId: string;
  question: string;
  slug: string;
  clobTokenIds: string; // JSON array
  outcomes: string; // JSON array
  outcomePrices: string; // JSON array
  endDate: string;
  closed: boolean;
  negRisk: boolean;
  umaResolutionStatus?: string;
  image?: string;
  icon?: string;
}

/**
 * Fetch recently closed/resolved Polymarket markets via our Next.js markets proxy.
 * If the trigger payload specifies a marketId, fetches that specific market.
 */
export const polymarketMarketCloseFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerPayload } = ctx;
  const marketId = triggerPayload.marketId as string | undefined;

  try {
    let markets: GammaMarket[] = [];

    if (marketId) {
      // Fetch specific market via proxy
      const param = marketId.startsWith("0x") ? "conditionId" : "slug";
      const res = await fetch(`/api/polymarket/markets?${param}=${encodeURIComponent(marketId)}`);
      if (!res.ok) return [];
      const all = (await res.json()) as GammaMarket[];
      // Gamma API can return unrelated markets — filter to exact match
      const idLower = marketId.toLowerCase();
      markets = all.filter(m =>
        marketId.startsWith("0x") ? m.conditionId.toLowerCase() === idLower : m.slug.toLowerCase() === idLower,
      );
    } else {
      // Fetch recently closed markets via proxy
      const params = new URLSearchParams({
        closed: "true",
        limit: "5",
        order: "endDate",
        ascending: "false",
      });
      const res = await fetch(`/api/polymarket/markets?${params}`);
      if (!res.ok) return [];
      markets = (await res.json()) as GammaMarket[];
    }

    if (!Array.isArray(markets) || !markets.length) return [];

    return markets.slice(0, 5).map(m => {
      let outcomes: string[] = [];
      let tokenIds: string[] = [];
      let outcomePrices: number[] = [];
      try {
        outcomes = JSON.parse(m.outcomes) as string[];
      } catch {
        /* empty */
      }
      try {
        tokenIds = JSON.parse(m.clobTokenIds) as string[];
      } catch {
        /* empty */
      }
      try {
        outcomePrices = (JSON.parse(m.outcomePrices) as string[]).map(p => parseFloat(p));
      } catch {
        /* empty */
      }

      // Determine winning outcome (price === 1)
      const winnerIndex = outcomePrices.findIndex(p => p === 1);
      const winningOutcome = winnerIndex >= 0 ? outcomes[winnerIndex] : null;
      const winningAssetID = winnerIndex >= 0 ? tokenIds[winnerIndex] : null;

      return {
        triggeredAt: new Date().toISOString(),
        market: {
          conditionId: m.conditionId,
          question: m.question,
          slug: m.slug,
          outcomes,
          closed: true,
          winningOutcome,
          winningAssetID,
          tokenIds,
          negRisk: m.negRisk ?? false,
        },
        _question: m.question,
        _winningOutcome: winningOutcome ?? "Pending",
        _closed: true,
        _icon: m.image || m.icon || "",
      };
    });
  } catch {
    return [];
  }
};
